import type { ReactElement } from "react";
import type { ApiClient, Konto } from "@kundeninfo/api-client";
import { Feld } from "@kundeninfo/ui";
import { Auswahl, Chip, Schalter } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../../components/meHuellen.js";
import { Loaded, MasterDataPanel, MasterDataTable, StateFlags, useMasterData } from "./masterData.js";
import { errorProp } from "../../../components/fieldErrors.js";
import { ACCOUNTS_TEXT as T } from "./texts.js";

/**
 * Verwaltung › KONTEN (Spec „Kontenliste“, E364: global): alle Konten samt deaktivierten, nach Kontenrahmen gruppiert,
 * Art und Zustand als Kennzeichen. Rahmen, Nummer und Art stehen nach dem Anlegen fest (P22) — geändert werden
 * Bezeichnung und „aktiv“; ein Konto, auf das kontiert ist („verwendet“), lässt sich nur deaktivieren.
 */
export type AccountsClient = Pick<ApiClient, "getBookingAccounts" | "createBookingAccount" | "updateBookingAccount">;

type Chart = Konto["chartOfAccounts"];
type AccountDraft = { id: string | null; version: number; chartOfAccounts: Chart; accountNumber: string; name: string;
  kind: Konto["kind"]; active: boolean };

const CHARTS: readonly Chart[] = ["SKR04", "SKR03"];
const KIND_TEXT: Record<Konto["kind"], string> = { REVENUE: T.revenue, EXPENSE: T.expense };
const LABELS = { chartOfAccounts: T.chart, accountNumber: T.accountNumber, name: T.name, kind: T.kind, active: T.active };

const toDraft = (k: Konto | null): AccountDraft => (k === null
  ? { id: null, version: 0, chartOfAccounts: "SKR04", accountNumber: "", name: "", kind: "REVENUE", active: true }
  : { id: k.id, version: k.version, chartOfAccounts: k.chartOfAccounts, accountNumber: k.accountNumber, name: k.name,
      kind: k.kind, active: k.active });

export function AccountsSection({ client }: { client: AccountsClient }): ReactElement {
  const m = useMasterData(() => client.getBookingAccounts({ includeInactive: true }), "", toDraft);
  const d = m.draft;

  async function save(): Promise<void> {
    if (d === null) return;
    const { id, version, ...e } = d;
    if (id === null) await client.createBookingAccount(e);
    else await client.updateBookingAccount(id, { version, name: e.name, active: e.active });
    await m.reload();
  }

  return (
    <div className="vwk">
      <div className="vw__leiste">
        <KiKnopf art="primaer" onClick={() => m.open(null)}>{T.newAccount}</KiKnopf>
      </div>
      <Loaded list={m}>{(rows) => CHARTS.filter((c) => rows.some((k) => k.chartOfAccounts === c)).map((chart) => (
        <section key={chart} className="vwk__gruppe">
          <h4 className="vwk__gruppe-titel">{T.groupChart(chart)}</h4>
          <MasterDataTable rows={rows.filter((k) => k.chartOfAccounts === chart)} onEdit={m.open}
            editName={(k) => T.editNamed(`${k.accountNumber} ${k.name}`)}
            columns={[
              { head: T.accountNumber, numeric: true, cell: (k) => k.accountNumber },
              { head: T.name, cell: (k) => k.name },
              { head: T.kind, cell: (k) => <Chip>{KIND_TEXT[k.kind]}</Chip> },
              { head: T.state, cell: (k) => <StateFlags row={k} /> },
            ]} />
        </section>
      ))}</Loaded>

      {d !== null && (
        <MasterDataPanel title={d.id === null ? T.newAccount : T.editAccount} kind={T.accountKind} name={d.name} dirty={m.dirty} deactivating={m.deactivating} save={save}
          refresh={m.refresh} onClose={m.close} labels={LABELS}>
          {(f) => (
            <>
              {d.id === null ? (
                <>
                  <Auswahl beschriftung={T.chart} wert={d.chartOfAccounts} {...errorProp(f.chartOfAccounts)}
                    onGeaendert={(e) => m.change({ chartOfAccounts: e.wert as Chart })}>
                    {CHARTS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </Auswahl>
                  <Feld id="vwk-accountNumber" label={T.accountNumber} bearbeitbar pflicht wert={d.accountNumber}
                    fehler={f.accountNumber} onChange={(w) => m.change({ accountNumber: w })} />
                </>
              ) : (
                // Rahmen, Nummer und Art stehen fest (P22) — lesend.
                ([[T.chart, d.chartOfAccounts], [T.accountNumber, d.accountNumber], [T.kind, KIND_TEXT[d.kind]]] as const)
                  .map(([label, wert]) => <Feld key={label} id={`vwk-${label}`} label={label} bearbeitbar={false}
                    wert={wert} onChange={() => undefined} />)
              )}
              <Feld id="vwk-name" label={T.name} bearbeitbar pflicht wert={d.name} fehler={f.name}
                onChange={(w) => m.change({ name: w })} />
              {d.id === null && (
                <Auswahl beschriftung={T.kind} wert={d.kind} {...errorProp(f.kind)}
                  onGeaendert={(e) => m.change({ kind: e.wert as Konto["kind"] })}>
                  {(["REVENUE", "EXPENSE"] as const).map((k) => <option key={k} value={k}>{KIND_TEXT[k]}</option>)}
                </Auswahl>
              )}
              <Schalter beschriftung={T.active} an={d.active} {...errorProp(f.active)}
                onGeaendert={(e) => m.change({ active: e.an })} />
            </>
          )}
        </MasterDataPanel>
      )}
    </div>
  );
}
